/**
 * usePushNotifications Hook
 *
 * The notification state shown to the user IS Apple's actual permission for
 * Fitxess ('granted' | 'denied' | 'prompt' | 'unsupported'), re-read on mount
 * and whenever the app returns to the foreground (the user can change it in
 * iPhone Settings at any time). There is no separate Fitxess on/off preference
 * yet, and a device token arriving is never treated as "enabled".
 */
import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import pushService from '../services/pushNotificationService';

export default function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('unknown');
  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);

  const refreshPermission = useCallback(async () => {
    const state = await pushService.getPermissionState();
    setPermission(state);
    return state;
  }, []);

  // Initialize on mount
  useEffect(() => {
    const supported = Capacitor.isNativePlatform();
    setIsSupported(supported);

    if (!supported) {
      setPermission('unsupported');
      return undefined;
    }

    let active = true;
    pushService.getPermissionState().then((state) => { if (active) setPermission(state); });

    // Re-read the real permission whenever the app returns to the foreground.
    let appStateHandle = null;
    Promise.resolve(App.addListener('appStateChange', (state) => {
      if (state && state.isActive && active) {
        pushService.getPermissionState().then((next) => { if (active) setPermission(next); });
      }
    })).then((handle) => {
      if (active) appStateHandle = handle;
      else if (handle) handle.remove();
    }).catch(() => {});

    // Set up listeners
    const unsubRegistration = pushService.addRegistrationListener((deviceToken) => {
      setToken(deviceToken);
      // TODO: Send token to backend for storage (deferred until Fitxess push delivery exists)
      console.log('[usePush] Device token received');
    });

    const unsubError = pushService.addRegistrationErrorListener((err) => {
      setError(err);
    });

    // Clean up on unmount
    return () => {
      active = false;
      if (appStateHandle) appStateHandle.remove();
      unsubRegistration();
      unsubError();
    };
  }, []);

  /**
   * Request permission (Apple's prompt appears only if never answered) and
   * register for push. Resolves true when permission is granted.
   */
  const enablePush = useCallback(async () => {
    if (!isSupported) {
      console.log('[usePush] Not supported on this platform');
      return false;
    }

    try {
      setError(null);
      const result = await pushService.initializePushNotifications();
      setPermission(result.permission || (result.granted ? 'granted' : await pushService.getPermissionState()));
      if (!result.granted) {
        setError(result.error || 'Permission denied');
      }
      return !!result.granted;
    } catch (err) {
      setError(err.message || 'Failed to enable push notifications');
      await refreshPermission();
      return false;
    }
  }, [isSupported, refreshPermission]);

  /**
   * Add a listener for incoming notifications
   */
  const onNotificationReceived = useCallback((callback) => {
    return pushService.addNotificationReceivedListener(callback);
  }, []);

  /**
   * Add a listener for notification taps
   */
  const onNotificationTapped = useCallback((callback) => {
    return pushService.addNotificationActionListener(callback);
  }, []);

  return {
    isSupported,
    permission,
    isEnabled: permission === 'granted',
    token,
    error,
    enablePush,
    refreshPermission,
    onNotificationReceived,
    onNotificationTapped,
  };
}
